import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Stat, Section, Loading, useFetch } from "./_shared";

const ICON = { pass: CheckCircle2, warn: AlertTriangle, fail: XCircle };
const COL = { pass: "text-emerald-600", warn: "text-amber-600", fail: "text-rose-600" };

export default function Launch() {
  const { data: lr, loading } = useFetch("/admin/launch-readiness");
  const { data: rollout, reload } = useFetch("/admin/rollout-status");
  const [store, setStore] = useState("ios");
  const [percent, setPercent] = useState("");
  const [busy, setBusy] = useState(false);
  if (loading) return <Loading />;
  const sum = lr?.summary || {}; const checks = lr?.checks || [];
  const storeState = (s) => rollout?.[s] || {};

  const post = async (action) => {
    setBusy(true);
    try { await api.post("/admin/rollout-status/events", { store, action, percent: percent ? Number(percent) : undefined }); toast.success("Event gespeichert."); setPercent(""); await reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Launch & Rollout" subtitle={lr?.headline || "Startbereitschaft prüfen."} />
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Bestanden" value={sum.pass ?? 0} tone="good" />
        <Stat label="Warnungen" value={sum.warn ?? 0} tone={sum.warn ? "warn" : "good"} />
        <Stat label="Fehler" value={sum.fail ?? 0} tone={sum.fail ? "bad" : "good"} />
      </div>

      <Section title="Launch-Readiness" testId="admin-launch-checks">
        <div className="space-y-1.5">
          {checks.map((c, i) => {
            const I = ICON[c.status] || CheckCircle2;
            return (
              <div key={i} className="flex items-center gap-2 rounded-xl border border-border bg-background p-2.5">
                <I className={`h-4 w-4 shrink-0 ${COL[c.status] || ""}`} />
                <span className="flex-1 text-sm text-foreground">{c.label}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{c.note}</span>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Rollout-Status" testId="admin-rollout">
        <div className="grid grid-cols-2 gap-3">
          {["ios", "android"].map((s) => (
            <div key={s} className="rounded-xl border border-border bg-background p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{s}</p>
              <p className="font-display text-2xl font-semibold text-foreground">{storeState(s).percent ?? 0}%</p>
              <p className="text-xs text-muted-foreground">{storeState(s).status || "not_started"}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={store} onChange={(e) => setStore(e.target.value)} className="h-9 rounded-lg border border-border bg-background px-2 text-sm text-foreground" data-testid="admin-rollout-store">
            <option value="ios">iOS</option><option value="android">Android</option><option value="both">Beide</option>
          </select>
          <Input type="number" value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="%" className="h-9 w-20" data-testid="admin-rollout-percent" />
          <Button size="sm" className="rounded-full" disabled={busy} onClick={() => post("increase")} data-testid="admin-rollout-increase">Erhöhen</Button>
          <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => post("pause")} data-testid="admin-rollout-pause">Pause</Button>
          <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => post("resume")} data-testid="admin-rollout-resume">Fortsetzen</Button>
        </div>
      </Section>
    </div>
  );
}
